import {ProductRemote} from "@app/core/product/domain/repositories/product.remote";
import {Observable} from "rxjs";
import {Injectable} from "@angular/core";
import {ProductSearchSuggestion} from "@app/core/product/domain/dtos/product-search-suggestion";
import {ProductSearchSuggestionMapper} from "@app/core/product/infrastructure/mappers/product-search-suggestion.mapper";
import {catchError, map, switchMap, take} from "rxjs/operators";
import {ProductListPage} from "@app/core/product/domain/dtos/product-list-page";
import {ProductListPageMapper} from "@app/core/product/infrastructure/mappers/product-list-page.mapper";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {ApiUrls} from "@app/core/shared/infrastructure/api-urls";
import {EuropastryBaseHandler} from "@app/core/shared/infrastructure/base.handler/europastry-base.handler";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {ProductDetails} from "@app/core/product/domain/dtos/product-details";
import {ProductDetailsMapper} from "@app/core/product/infrastructure/mappers/product-details.mapper";
import {Product} from "@app/core/product/domain/dtos/product";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {DeliveryPointLocal} from "@app/core/delivery-point/domain/delivery-point.local";
import {ProductMapper} from "@app/core/product/infrastructure/mappers/product.mapper";
import {ProductFilter} from "../domain/dtos/product-filter";
import {ProductFilterMapper} from "./mappers/product-filter.mapper";
import {ProductNotExistsError} from "../domain/error/product-not-exists.error";

@Injectable({
    providedIn: "root",
})

export class ProductWebService implements ProductRemote {

    constructor(
        private baseHandler: EuropastryBaseHandler,
        private credentialsStorage: CredentialsStorage,
        private deliveryPointLocal: DeliveryPointLocal,
        private apiUrls: ApiUrls,
    ) { }

    getSearchSuggestions(query: string): Observable<ProductSearchSuggestion[]> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            let requestBody = { "search": query };
            let limit = 10;

            const parameters = new HandlerBuilder()
                .method(HttpMethod.post)
                .url(this.apiUrls.SEARCH_AUTOCOMPLETE + deliveryPointCode + '/' + limit)
                .body(requestBody)
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map((suggestions: any[]) => suggestions.map(ProductSearchSuggestionMapper.deserialize)),
                catchError(BaseHandleErrorMapper.handleError),

            );
        }));
    }

    getProductDetails(productCode: string): Observable<ProductDetails> {

        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(
            take(1), // It is used to avoid multiple requests when the user changes the delivery point
            switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .authorization(this.credentialsStorage.getCredentials())
                .url(this.apiUrls.PRODUCT_DETAILS + deliveryPointCode + '/' + productCode)
                .build()

            return this.baseHandler.execute(parameters).pipe(
                catchError(BaseHandleErrorMapper.handleError),
                map(productDetails => {
                    if (productDetails.results==null) {
                        throw new ProductNotExistsError();
                    }
                    return ProductDetailsMapper.deserialize(productDetails.results)
                }),
            );
        }));
    }

    getSimilarProducts(productCode: string, itemsPerPage: number, page: number): Observable<Product[]> {

        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.RELATED_PRODUCTS + productCode + '/' + deliveryPointCode + '/' + itemsPerPage + '/' + page)
                .authorization(this.credentialsStorage.getCredentials())
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(productsPage => productsPage.results.map(ProductMapper.deserialize)),
                catchError(BaseHandleErrorMapper.handleError),
            );
        }));
    }

    getSuggestedProducts(deliveryPointCode: number): Observable<Product[]> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(this.apiUrls.SUGGESTED_PRODUCTS + deliveryPointCode + '/' + 'R')
            .authorization(this.credentialsStorage.getCredentials())
            .build()

        return this.baseHandler.execute(parameters).pipe(
            map(productsPage => productsPage.results.map(ProductMapper.deserialize)),
            catchError(BaseHandleErrorMapper.handleError),
        );
    }

    getProductList(itemsPerPage: number, page: number, selectedFilters: string, selectedSorting: string,
        categoryCode: string, specialTypeCode: string, query: string): Observable<ProductListPage> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const formattedQuery = this.encodeQuery(query);

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.LIST_PRODUCTS + selectedSorting + '/'
                    + deliveryPointCode + '/' + itemsPerPage + '/' + page + '/'
                    + selectedFilters + '/' + categoryCode + '/' + specialTypeCode + '/' + formattedQuery)
                .authorization(this.credentialsStorage.getCredentials())
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(response => ProductListPageMapper.deserialize(response)),
                catchError(BaseHandleErrorMapper.handleError)
            )
        }));
    }

    getFilters(selectedFilters: string, categoryCode: string, specialTypeCode: string, query: string): Observable<ProductFilter[]> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const formattedQuery = this.encodeQuery(query);

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.FILTERS + deliveryPointCode + '/' + selectedFilters + '/'
                    + categoryCode + '/' + specialTypeCode + '/' + formattedQuery)
                .authorization(this.credentialsStorage.getCredentials())
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(response => response.results.map(ProductFilterMapper.deserialize)),
                catchError(BaseHandleErrorMapper.handleError)
            )
        }));
    }

    getProductByEan(productEan: string): Observable<ProductDetails> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(
            take(1),
            switchMap(deliveryPointCode => {

                const parameters = new HandlerBuilder()
                    .method(HttpMethod.get)
                    .authorization(this.credentialsStorage.getCredentials())
                    .url(this.apiUrls.PRODUCT_BY_EAN + deliveryPointCode + '/' + productEan)
                    .build()

                return this.baseHandler.execute(parameters).pipe(
                    catchError(BaseHandleErrorMapper.handleError),
                    map(productDetails => {
                        if (productDetails==null) {
                            throw new ProductNotExistsError();
                        }
                        return ProductDetailsMapper.deserialize(productDetails)
                    }),
                );
            }));
    }

    private encodeQuery(query: string) {
        return encodeURIComponent(query.replace(' ', ','));
    }
}
